import { DefaultVideoTransformDevice, VideoFxProcessor } from "amazon-chime-sdk-js";
import type { Logger, VideoFrameProcessor } from "amazon-chime-sdk-js";
import backgroundUrl from "../assets/keelung-video-background.v1.svg";

// Remounts share a lease; failed cleanup quarantines it for this module's lifetime.
let backgroundLifecycleLease: { quarantined: boolean } | null = null;

export interface ProtectedVideoInput {
  device: DefaultVideoTransformDevice;
  ready: Promise<void>;
  dispose: () => Promise<void>;
}

/** Never let an SDK NoOp/fail-open buffer reach the transform's output canvas. */
export function protectBackgroundOutput(
  processor: VideoFrameProcessor,
  onFrame: () => void,
  onFailure: () => void,
  isCancelled: () => boolean,
): VideoFrameProcessor {
  let destruction: Promise<void> | undefined;
  return {
    async process(buffers) {
      try {
        if (isCancelled()) throw new Error("背景處理已取消");
        const inputs = new Set(buffers);
        const canvases = new Set(buffers.map((buffer) => buffer.asCanvasElement?.()).filter(Boolean));
        const output = await processor.process([...buffers]);
        if (isCancelled() || !output.length || output.some((buffer) => {
          if (!buffer || inputs.has(buffer)) return true;
          const canvas = buffer.asCanvasElement?.();
          // SDK 3.32 CanvasVideoFrameBuffer leaves width/height fields unset.
          return !(canvas?.width ?? buffer.width) || !(canvas?.height ?? buffer.height)
            || Boolean(canvas && canvases.has(canvas));
        })) {
          throw new Error("背景處理未產生受保護影像");
        }
        onFrame();
        return output;
      } catch (error) {
        onFailure();
        throw error;
      }
    },
    destroy() {
      if (!destruction) destruction = Promise.resolve().then(() => processor.destroy());
      return destruction;
    },
    setEventController: (controller) => processor.setEventController?.(controller),
  };
}

export async function loadVideoBackground(signal: AbortSignal): Promise<{ url: string; dispose: () => void }> {
  const response = await fetch(backgroundUrl, { signal, credentials: "same-origin" });
  if (!response.ok || !response.headers.get("content-type")?.startsWith("image/")) {
    throw new Error("固定背景載入失敗，攝影機保持關閉。");
  }
  const url = URL.createObjectURL(await response.blob());
  const dispose = () => URL.revokeObjectURL(url);
  try {
    await new Promise<void>((resolve, reject) => {
      const image = new Image();
      const finish = (error?: Error) => {
        signal.removeEventListener("abort", abort);
        image.onload = null;
        image.onerror = null;
        if (error) { image.src = ""; reject(error); }
        else resolve();
      };
      const abort = () => finish(new DOMException("Camera cancelled", "AbortError"));
      image.onload = () => finish(image.naturalWidth > 0 && image.naturalHeight > 0
        ? undefined : new Error("固定背景尺寸無效"));
      image.onerror = () => finish(new Error("固定背景無法解碼"));
      if (signal.aborted) { abort(); return; }
      signal.addEventListener("abort", abort, { once: true });
      image.src = url;
    });
    return { url, dispose };
  } catch (error) { dispose(); throw error; }
}

/** A cancelled/timed-out model retains this slot until its late destruction finishes. */
export class ProtectedVideoInputFactory {
  async create(logger: Logger, stream: MediaStream, signal: AbortSignal, onFailure: () => void): Promise<ProtectedVideoInput> {
    if (backgroundLifecycleLease) {
      throw new Error(backgroundLifecycleLease.quarantined
        ? "背景保護資源清理失敗，無法確認已回收；請重新載入頁面後重試，仍可使用語音。"
        : "背景保護仍在準備或釋放資源，請稍後重試去背。");
    }
    const lease = { quarantined: false };
    backgroundLifecycleLease = lease;
    const releaseLease = () => {
      if (backgroundLifecycleLease === lease) backgroundLifecycleLease = null;
    };
    let background: Awaited<ReturnType<typeof loadVideoBackground>> | undefined;
    let processor: VideoFrameProcessor | undefined;
    const assertActive = () => {
      if (signal.aborted) throw new DOMException("Camera cancelled", "AbortError");
    };
    try {
      if (!await VideoFxProcessor.isSupported(logger, false)) {
        throw new Error("此裝置不支援去背，攝影機保持關閉；仍可使用語音。");
      }
      assertActive();
      background = await loadVideoBackground(signal);
      assertActive();
      try {
        processor = await VideoFxProcessor.create(logger, {
          backgroundBlur: { isEnabled: false, strength: "low" },
          backgroundReplacement: { isEnabled: true, backgroundImageURL: background.url, defaultColor: undefined },
        }, 50);
      } catch {
        throw new Error("背景模型載入失敗，攝影機保持關閉；可重試去背或只用語音。");
      }
      assertActive();
      let disposed = false;
      let failed = false;
      let watchdog: ReturnType<typeof setTimeout> | undefined;
      let firstFrame!: () => void;
      let firstStart!: () => void;
      let rejectReady!: (error: Error) => void;
      const verified = new Promise<void>((resolve, reject) => { firstFrame = resolve; rejectReady = reject; });
      const started = new Promise<void>((resolve) => { firstStart = resolve; });
      const ready = Promise.all([verified, started]).then(() => undefined);
      void ready.catch(() => undefined);
      const fail = () => {
        if (disposed || failed || signal.aborted) return;
        failed = true;
        clearTimeout(watchdog);
        rejectReady(new Error("背景影像處理已停止，攝影機關閉；仍可使用語音。"));
        onFailure();
      };
      const armWatchdog = () => {
        clearTimeout(watchdog);
        watchdog = setTimeout(fail, 15_000);
      };
      const guarded = protectBackgroundOutput(processor, () => {
        firstFrame();
        armWatchdog();
      }, fail, () => disposed || failed || signal.aborted);
      const device = new DefaultVideoTransformDevice(logger, stream, [guarded]);
      device.framerate = 15;
      const observer = {
        processingDidStart: () => firstStart(),
        processingDidFailToStart: fail,
        processingDidStop: fail,
      };
      device.addObserver(observer);
      armWatchdog();
      let disposal: Promise<void> | undefined;
      const dispose = () => {
        if (disposal) return disposal;
        disposed = true;
        clearTimeout(watchdog);
        rejectReady(new Error("背景處理已取消"));
        device.removeObserver(observer);
        disposal = (async () => {
          try {
            try { await device.stop(); }
            finally {
              try { await guarded.destroy(); }
              finally { background?.dispose(); }
            }
            releaseLease();
          } catch (error) {
            lease.quarantined = true;
            throw error;
          }
        })();
        return disposal;
      };
      return { device, ready, dispose };
    } catch (error) {
      try {
        try { await processor?.destroy(); }
        finally { background?.dispose(); }
      } catch (cleanupError) {
        lease.quarantined = true;
        throw cleanupError;
      }
      releaseLease();
      throw error;
    }
  }
}
